/// <amd-module name="Feed/Legacy/Components/ConnectToFeedButton" />
import * as React from "react";
import { connect } from "react-redux";

import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import { Button } from "VSSUI/Button";

import { CiConstants } from "Feed/Common/Constants/Constants";
import { FeedView } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { HubAction } from "Feed/Legacy/Types/IHubState";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IConnectToFeedButtonProps {
    pageContext: IVssPageContext;
    feed: Feed;
    feedViews: FeedView[];
    protocolMap: { [key: string]: IPackageProtocol };
    isCallToAction?: boolean;
    hubAction: HubAction;
    defaultTabId?: string;
}

const Actions = {
    showConnectToFeed: FeedHubActions.connectToFeed
};

function useNewFeedUi(pageContext: IVssPageContext): boolean {
    return isFeatureFlagEnabled(pageContext, "Packaging.Feed.ConnectToFeed.EnableUI");
}

export const _ConnectToFeedButton: React.FC<IConnectToFeedButtonProps & typeof Actions> = props => (
    <Button
        primary={!!props.isCallToAction}
        className="connect-to-feed-button"
        onClick={() => {
            if (useNewFeedUi(props.pageContext)) {
                props.showConnectToFeed();
            } else {
                OpenConnectToFeedDialog(
                    props.pageContext,
                    props.feed,
                    props.feedViews,
                    props.protocolMap,
                    props.hubAction,
                    props.defaultTabId
                );
            }
        }}
    >
        <span className="bowtie-icon bowtie-plug-outline connect-to-feed-button-icon" />
        {PackageResources.ConnectToFeedButtonText}
    </Button>
);

export const ConnectToFeedButton = connect<IConnectToFeedButtonProps, typeof Actions, IConnectToFeedButtonProps>(
    (state, ownProps) => ({ ...ownProps }),
    Actions
)(_ConnectToFeedButton);

export function OpenConnectToFeedDialog(
    pageContext: IVssPageContext,
    feed: Feed,
    feedViews: FeedView[],
    protocolMap: { [key: string]: IPackageProtocol },
    hubAction: HubAction,
    defaultTabId?: string
): void {
    let filteredMap: { [key: string]: IPackageProtocol } = {};
    if (feed && feed.project != null) {
        Object.keys(protocolMap).forEach((key: string) => {
            if (protocolMap[key].supportedFeatures(feed).projectScopedFeeds) {
                filteredMap[key] = protocolMap[key];
            }
        });
    } else {
        filteredMap = protocolMap;
    }

    const legacyPlatformService = pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
    legacyPlatformService
        .loadModules(
            ["Package/Scripts/Dialogs/ConnectToFeedDialog"],
            ["ms.feed.package-service-context-data-provider", "ms.feed.package-hub-data-provider"]
        )
        .then(() => {
            legacyPlatformService
                .requireModules(["VSS/Controls/Dialogs", "Package/Scripts/Dialogs/ConnectToFeedDialog"])
                .then(([dialogsModule, connectToFeedDialogModule]) => {
                    dialogsModule.ModalDialogO.show(connectToFeedDialogModule.ConnectToFeedDialog, {
                        feed,
                        feedViews,
                        protocolDictionary: filteredMap,
                        hubAction,
                        defaultTabId
                    });
                    const defaultProtocolKey = Object.keys(filteredMap)[0];
                    CustomerIntelligenceHelper.publishEvent(pageContext, CiConstants.ConnectToFeed, {
                        action: CiConstants.ConnectToFeedOpened,
                        protocol: defaultProtocolKey,
                        context: HubAction[hubAction]
                    });

                    const element = document.getElementsByClassName("tab-title current")[0] as HTMLElement;
                    element.focus();
                });
        });
}
